/**
 * Custom Dirham Currency Symbol Fix
 *
 * Removes "AED" / Arabic currency text from price displays and trims trailing ".00".
 * The dirham glyph (U+E006) is added via CSS ::before on .money / .price-item /
 * .totals__total-value / standalone .price elements.
 */

const PRICE_SELECTORS = [
  '.money',
  '.price',
  '.price-item',
  '.price-per-item',
  '.unit-price',
  '.totals__total-value',
  '.totals__subtotal-value',
  '.cart-item__totals',
  '[class*="bcpo-cart-item-price"]',
  '[class*="bcpo-cart-item-line-price"]',
  '[class*="bcpo-cart-item-original-price"]',
  '[class*="bcpo-cart-original-total"]',
  '[class*="bcpo-cart-final-total"]',
  '[class*="active-facets__button"]',
].join(',');

const AED_RE = /\s*AED\s*/gi;
const ARABIC_AED_RE = /\s*د\.إ\s*/g;
const TRAILING_ZEROS_RE = /(\d)\.00\b/g;

function clean(text) {
  if (!text) return text;
  if (!text.trim()) return text;
  const cleaned = text
    .replace(AED_RE, '')
    .replace(ARABIC_AED_RE, '')
    .replace(TRAILING_ZEROS_RE, '$1')
    .replace(/[ \t]{2,}/g, ' ');
  return cleaned;
}

function cleanTextNodesIn(root) {
  if (!root || root.nodeType !== Node.ELEMENT_NODE) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    const original = node.textContent;
    const cleaned = clean(original);
    if (cleaned !== original) node.textContent = cleaned;
  }
}

function cleanAll() {
  document.querySelectorAll(PRICE_SELECTORS).forEach(cleanTextNodesIn);
}

function isPriceElement(el) {
  return el && el.nodeType === Node.ELEMENT_NODE && typeof el.matches === 'function' && el.matches(PRICE_SELECTORS);
}

function findPriceAncestor(node) {
  let el = node && node.nodeType === Node.ELEMENT_NODE ? node : node && node.parentElement;
  while (el && el !== document.body) {
    if (isPriceElement(el)) return el;
    el = el.parentElement;
  }
  return null;
}

// Initial sweep
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', cleanAll);
} else {
  cleanAll();
}

// Re-run on lifecycle events
document.addEventListener('shopify:section:load', cleanAll);
document.addEventListener('cart:updated', cleanAll);

// Debounce repeated mutations within a single frame
let cleanScheduled = false;
function scheduleClean() {
  if (cleanScheduled) return;
  cleanScheduled = true;
  requestAnimationFrame(() => {
    cleanScheduled = false;
    cleanAll();
  });
}

const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    if (mutation.type === 'characterData') {
      if (findPriceAncestor(mutation.target)) {
        scheduleClean();
        return;
      }
    } else if (mutation.type === 'childList') {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          if (isPriceElement(node) || (node.querySelector && node.querySelector(PRICE_SELECTORS))) {
            scheduleClean();
            return;
          }
        } else if (node.nodeType === Node.TEXT_NODE && findPriceAncestor(node)) {
          scheduleClean();
          return;
        }
      }
    }
  }
});

observer.observe(document.body, {
  childList: true,
  subtree: true,
  characterData: true,
});
